/**
 * The one place a presigned PUT is performed.
 *
 * WHY THIS EXISTS AS A CHOKEPOINT RATHER THAN A FIX AT EACH CALL SITE
 *
 * `fetch()` does not reject on an HTTP error status. A 403 from an expired
 * presigned URL, a 400 from a content-type mismatch, a 500 from the bucket —
 * all of them *resolve*, with `ok === false`. Code that writes
 *
 *     await fetch(uploadUrl, { method: 'PUT', body: file });
 *     await apiClient.post('/uploads/confirm', { key, size });
 *
 * therefore confirms an object that is not in the bucket and renders success.
 * Nothing about that reads as wrong on the page; it is only wrong at 3am when
 * somebody goes looking for the ticket photo.
 *
 * The check itself is one line, and three of the seven call sites in this repo
 * already had it. That is precisely the argument for a chokepoint: a rule that
 * has to be remembered independently at every call site has already been
 * forgotten at four of them, and the next upload feature would have been the
 * fifth. Every presigned PUT now goes through this function, so `res.ok`, the
 * timeout, and the error shape are inherited rather than re-derived — the same
 * reasoning `getDB()` uses for its internal open guard.
 *
 * NO RAW `fetch` ELSEWHERE. `apiClient` (axios) cannot be used for the PUT
 * itself: the presigned URL is an absolute S3/R2 URL, and sending it through
 * the client would attach the tenant `Authorization` header, which S3 rejects
 * as a conflicting auth mechanism. So the PUT stays a `fetch` — but exactly
 * one of them, living in `src/api/`, with the same guarantees the axios
 * interceptor gives every other request.
 */

import { UPLOAD_TIMEOUT_MS } from './client';

/**
 * Budget for a single presigned PUT.
 *
 * Deliberately the same 300s `apiClient` already gives a FormData body
 * ("photos/docs off a truck on one bar", PR #289) — an upload is an upload,
 * and having two different answers to "how long may a field photo take" is how
 * they drift apart.
 *
 * Sizing: `compressImage` targets 200-500KB for a field photo, and the largest
 * thing that reaches this function is an uncompressed cert PDF at the 10MB cap.
 * One bar of usable LTE sustains roughly 100-200 kbit/s upstream; 500KB is ~20-40s
 * there and 300s covers even the 10MB PDF at the low end of that range with
 * room for a stall or two. The number is not tuned to be tight — it is tuned to
 * be *finite*. Before this, a stalled PUT never settled at all: the spinner in
 * PhotoUploader/SignatureCapture/FieldCertsScreen ran until the app was killed,
 * because `setUploading(false)` lives in a `finally` that was never reached.
 */
export const PRESIGNED_PUT_TIMEOUT_MS = UPLOAD_TIMEOUT_MS;

const UPLOAD_FAILED_FLAG = '__fieldiqUploadFailed';

/**
 * Why the upload did not land. Callers that queue for retry need this — a
 * `network` failure is worth retrying unchanged, a `rejected` one is not.
 *
 * - `rejected`  — S3 answered 4xx. The request is wrong (expired URL, bad
 *                 signature, content-type mismatch). Byte-identical retries fail.
 * - `server`    — S3 answered 5xx. Transient; the same bytes may well succeed.
 * - `network`   — no response at all. Nothing was rejected; we never got there.
 * - `timeout`   — no response inside the budget. Treated as `network`'s cousin.
 */
export type UploadFailureKind = 'rejected' | 'server' | 'network' | 'timeout';

export interface UploadFailedError extends Error {
  __fieldiqUploadFailed: true;
  kind: UploadFailureKind;
  /** HTTP status S3 answered with. Absent when there was no response. */
  status?: number;
  /** A sentence safe to show a user. `getApiErrorMessage` reads this field. */
  userMessage: string;
}

/**
 * Property check rather than `instanceof`: tsconfig targets es5, where
 * subclassing Error breaks the prototype chain (same reasoning as
 * `isTimeoutError` in `lib/with-timeout`).
 */
export function isUploadFailedError(err: unknown): err is UploadFailedError {
  return !!err && (err as any)[UPLOAD_FAILED_FLAG] === true;
}

/** True when the failure is worth retrying with the same bytes. */
export function isRetryableUploadError(err: unknown): boolean {
  if (!isUploadFailedError(err)) return true; // unknown shape — do not discard work
  return err.kind !== 'rejected';
}

function uploadFailed(
  kind: UploadFailureKind,
  message: string,
  userMessage: string,
  status?: number
): UploadFailedError {
  const err = new Error(message) as UploadFailedError;
  err.name = 'UploadFailedError';
  (err as any)[UPLOAD_FAILED_FLAG] = true;
  err.kind = kind;
  err.userMessage = userMessage;
  if (status !== undefined) err.status = status;
  return err;
}

export interface PresignedPutOptions {
  /** Override the default budget. */
  timeoutMs?: number;
  /** Caller's own cancellation (component unmount, user cancel). */
  signal?: AbortSignal;
}

/**
 * PUT `body` to a presigned URL, or throw.
 *
 * Resolving means S3 answered 2xx. There is no other way out of this function
 * that returns normally, which is the whole point: a caller may go on to
 * `/uploads/confirm` and render success only on the resolve path.
 */
export async function putToPresignedUrl(
  uploadUrl: string,
  body: Blob,
  contentType: string,
  options: PresignedPutOptions = {}
): Promise<void> {
  const timeoutMs = options.timeoutMs ?? PRESIGNED_PUT_TIMEOUT_MS;
  const controller = new AbortController();

  // Distinguishes "we gave up" from "the caller cancelled" — the abort itself
  // looks identical from `fetch`'s side.
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  const forwardAbort = () => controller.abort();
  const external = options.signal;
  if (external) {
    if (external.aborted) controller.abort();
    else external.addEventListener('abort', forwardAbort);
  }

  let res: Response;
  try {
    res = await fetch(uploadUrl, {
      method: 'PUT',
      body,
      headers: { 'Content-Type': contentType },
      signal: controller.signal,
    });
  } catch (err: any) {
    if (timedOut) {
      throw uploadFailed(
        'timeout',
        `Presigned PUT did not settle within ${timeoutMs}ms`,
        'Upload timed out. Check your connection and try again.'
      );
    }
    // The caller cancelled deliberately — not a failure, and not ours to relabel.
    if (external?.aborted) throw err;
    throw uploadFailed(
      'network',
      `Presigned PUT failed: ${err?.message || 'network error'}`,
      'Upload failed — no connection to storage. Try again.'
    );
  } finally {
    clearTimeout(timer);
    if (external) external.removeEventListener('abort', forwardAbort);
  }

  if (!res.ok) {
    const kind: UploadFailureKind = res.status >= 500 ? 'server' : 'rejected';
    throw uploadFailed(
      kind,
      `S3 upload failed: ${res.status}`,
      res.status >= 500
        ? 'Storage is temporarily unavailable. Try again in a moment.'
        : `Upload was rejected by storage (${res.status}). Try again.`,
      res.status
    );
  }
}
